import styled from 'styled-components';
import colors from './colors';
import { breakpoints, maxBreakpoints } from './breakpoints';

// ==============================================
// TEXT COMPONENTS
// ==============================================

const BaseText = styled.div`
  text-align: center;
  margin: 0;
`;

export const Title = styled(BaseText).attrs({ as: 'h1' })`
  font-size: 32px;
  color: ${colors.white};
`;

export const Subtitle = styled(BaseText).attrs({ as: 'h2' })`
  font-size: 28px;
  color: ${colors.black};
`;

export const H3 = styled(BaseText).attrs({ as: 'h3' })`
  font-size: 22px;
  color: ${colors.black};
`;

// ==============================================
// FORM TEXT COMPONENTS
// ==============================================

export const FormTitle = styled.h2`
  font-size: 24px;
  font-weight: 800;
  color: ${colors.black};
  text-align: center;
  margin-top: 10px;
  margin-bottom: 18px;
  font-family: Mulish;
  line-height: 1.1;
  transition: all 0.3s ease;

  /* Mobile M (375px+) */
  @media ${breakpoints.mobileM} {
    margin-top: 12px;
    margin-bottom: 20px;
    font-size: 28px;
  }

  /* Mobile L (425px+) */
  @media ${breakpoints.mobileL} {
    margin-top: 13px;
    margin-bottom: 21px;
    font-size: 32px;
  }

  /* Tablet (768px+) */
  @media ${breakpoints.tablet} {
    margin-top: 14px;
    margin-bottom: 23px;
    font-size: 36px;
    line-height: 1.2;
  }

  /* Laptop (1024px+) */
  @media ${breakpoints.laptop} {
    margin-top: 16px;
    margin-bottom: 25px;
    font-size: 40px;
  }

  /* Laptop M (1366px+) */
  @media ${breakpoints.laptopM} {
    margin-top: 18px;
    margin-bottom: 27px;
    font-size: 42px;
  }
`;

export const ErrorText = styled.p`
  font-size: 14px;
  color: ${colors.error};
  margin-top: 4px;
  margin-bottom: 0;
  font-family: Mulish;
  text-align: ${(props) => (props.center ? 'center' : 'left')};
`;

// ==============================================
// FORM INPUT COMPONENTS
// ==============================================

export const FormInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid ${colors.inputBorderGray};
  border-radius: 4px;
  font-size: 16px;
  font-family: Mulish;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
  box-sizing: border-box;
  min-height: 48px;
  background-color: ${colors.white};

  &:focus {
    outline: none;
    border-color: ${colors.inputFocusRed};
    box-shadow: 0 0 0 2px ${colors.inputFocusRedAlpha};
  }

  &:hover {
    border-color: ${colors.inputHoverGray};
  }

  &:disabled {
    background-color: ${colors.inputHighlightBg};
    color: ${colors.gray04};
    cursor: not-allowed;
  }

  &::placeholder {
    color: ${colors.inputPlaceholderGray};
  }
`;

export const FormLabel = styled.label`
  display: block;
  font-size: 16px;
  font-weight: 700;
  color: ${colors.black};
  margin-bottom: 8px;
  font-family: Mulish;
  transition: all 0.3s ease;

  /* Mobile adjustments */
  @media ${maxBreakpoints.mobileL} {
    font-size: 14px;
    margin-bottom: 6px;
  }

  @media ${breakpoints.mobileM} {
    font-size: 15px;
    margin-bottom: 7px;
  }

  @media ${breakpoints.mobileL} {
    font-size: 15px;
    margin-bottom: 7px;
  }

  /* Tablet and above */
  @media ${breakpoints.tablet} {
    font-size: 16px;
    margin-bottom: 8px;
  }

  @media ${breakpoints.laptop} {
    font-size: 17px;
    margin-bottom: 10px;
  }

  @media ${breakpoints.laptopM} {
    font-size: 18px;
    margin-bottom: 12px;
  }
`;

// ==============================================
// BADGE / CHIP COMPONENTS
// ==============================================

export const Badge = styled.div`
  margin-top: ${(props) => props.marginTop || '40px'};
  width: ${(props) => props.width || '100px'};
  height: ${(props) => props.height || '26px'};
  background-color: ${(props) => props.bgColor || colors.blue03};
  color: ${(props) => props.textColor || colors.blue01};
  border-radius: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 14px;
  font-weight: ${(props) => props.fontWeight || '700'};
  font-family: Mulish;
  transition: all 0.3s ease;

  /* Mobile M (375px+) */
  @media ${breakpoints.mobileM} {
    margin-top: ${(props) =>
      props.marginTop ? `calc(${props.marginTop} + 5px)` : '45px'};
    width: ${(props) => (props.width ? `calc(${props.width} + 5px)` : '105px')};
    height: ${(props) =>
      props.height ? `calc(${props.height} + 1px)` : '27px'};
    font-size: 15px;
  }

  /* Mobile L (425px+) */
  @media ${breakpoints.mobileL} {
    margin-top: ${(props) =>
      props.marginTop ? `calc(${props.marginTop} + 10px)` : '50px'};
    width: ${(props) =>
      props.width ? `calc(${props.width} + 10px)` : '110px'};
    height: ${(props) =>
      props.height ? `calc(${props.height} + 2px)` : '28px'};
  }

  /* Tablet (768px+) */
  @media ${breakpoints.tablet} {
    margin-top: ${(props) =>
      props.marginTop ? `calc(${props.marginTop} + 15px)` : '55px'};
    width: ${(props) =>
      props.width ? `calc(${props.width} + 15px)` : '115px'};
    height: ${(props) =>
      props.height ? `calc(${props.height} + 4px)` : '30px'};
    font-size: 16px;
  }

  /* Laptop (1024px+) */
  @media ${breakpoints.laptop} {
    margin-top: ${(props) =>
      props.marginTop ? `calc(${props.marginTop} + 20px)` : '60px'};
    width: ${(props) =>
      props.width ? `calc(${props.width} + 20px)` : '120px'};
    height: ${(props) =>
      props.height ? `calc(${props.height} + 6px)` : '32px'};
    font-size: 17px;
  }
`;

// Alias for step indicator (used in Onboarding)
export const StepIndicator = styled(Badge)`
  margin-top: 0;
  margin-bottom: 47px;
  padding: 6px 16px;
  border-radius: 20px;
  width: auto;
  height: auto;
  font-size: 14px;
  font-weight: 600;
`;

// ==============================================
// BUTTON COMPONENTS
// ==============================================

const BaseButton = styled.button`
  font-size: 16px;
  font-weight: 500;
  font-family: Mulish;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: solid 1px transparent;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

export const Button = styled(BaseButton)`
  background-color: ${(props) =>
    props.disabled ? colors.lightgray : colors.red};
  padding: 14px 78px;
  color: ${colors.white};

  &:hover {
    background-color: ${colors.red01};
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:active {
    background-color: ${colors.red};
    transform: translateY(1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

export const CancelButton = styled(BaseButton)`
  background-color: transparent;
  padding: 14px 78px;
  color: ${colors.red};
  border: solid 1px ${colors.red};

  &:hover {
    background-color: ${colors.lightred};
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:active {
    background-color: ${colors.white};
    transform: translateY(1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

export const SmallButton = styled(BaseButton)`
  background-color: ${colors.red};
  padding: 7px 35px;
  color: ${colors.white};
  max-width: 100px;
`;

export const SmallCancelButton = styled(BaseButton)`
  background-color: transparent;
  padding: 7px 35px;
  color: ${colors.red};
  max-width: 100px;
  margin: 0 auto;
`;
